import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import { Users, Zap, Star, Globe, Shield, CheckCircle, ArrowRight } from "lucide-react";

const AboutPage = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  
  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-sky-50 to-white pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Outfit'] mb-6">
              {lang === "fr" ? "À propos de LëtzServices" : "About LëtzServices"}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {lang === "fr" 
                ? "Connecter les Luxembourgeois, un service à la fois." 
                : "Connecting Luxembourgers, one service at a time."}
            </p>
          </div>

          {/* Who We Are */}
          <Card className="bg-white rounded-3xl shadow-lg mb-8 overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-sky-100 rounded-2xl">
                  <Users className="w-8 h-8 text-sky-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">
                  {lang === "fr" ? "Qui sommes-nous ?" : "Who are we?"}
                </h2>
              </div>
              <p className="text-slate-600 text-lg leading-relaxed">
                {lang === "fr" 
                  ? "LëtzServices est une jeune plateforme de mise en relation entre prestataires de services et particuliers au Luxembourg. Notre objectif ? Offrir un moyen 100% gratuit de connecter les Luxembourgeois en créant un vaste réseau local. Notre site propose également une marketplace intégrée pour acheter et vendre facilement entre voisins."
                  : "LëtzServices is a young platform connecting service providers and individuals in Luxembourg. Our goal? To offer a 100% free way to connect Luxembourgers by creating a vast local network. Our site also features an integrated marketplace to easily buy and sell between neighbors."}
              </p>
            </div>
          </Card>

          {/* Our Mission */}
          <Card className="bg-white rounded-3xl shadow-lg mb-8 overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-green-100 rounded-2xl">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">
                  {lang === "fr" ? "Notre mission" : "Our Mission"}
                </h2>
              </div>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {lang === "fr"
                  ? "Créer un vaste réseau luxembourgeois pour connecter rapidement les gens."
                  : "Create a vast Luxembourg network to quickly connect people."}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                {lang === "fr"
                  ? "Nous avons constaté que beaucoup de personnes au Luxembourg font face au même problème : le manque d'information. Personne ne sait vraiment où se diriger pour trouver un service fiable ou proposer ses compétences. C'est pour cela que nous sommes là."
                  : "We noticed that many people in Luxembourg face the same problem: lack of information. Nobody really knows where to go to find a reliable service or offer their skills. That's why we're here."}
              </p>
            </div>
          </Card>

          {/* Why LëtzServices */}
          <Card className="bg-white rounded-3xl shadow-lg mb-8 overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-purple-100 rounded-2xl">
                  <Star className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">
                  {lang === "fr" ? "Pourquoi LëtzServices ?" : "Why LëtzServices?"}
                </h2>
              </div>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {lang === "fr"
                  ? "Chez nous, votre travail et la qualité de vos services influencent directement les avis de la communauté. Ces avis créent votre réputation et contribuent à l'évolution de notre réseau."
                  : "With us, your work and service quality directly influence community reviews. These reviews build your reputation and contribute to our network's growth."}
              </p>
              <div className="bg-sky-50 rounded-2xl p-6 border-l-4 border-sky-500">
                <p className="text-sky-800 font-medium text-lg">
                  {lang === "fr"
                    ? "🚀 L'évolution de la communauté commence par une inscription sur le site !"
                    : "🚀 The community's evolution starts with signing up on the site!"}
                </p>
              </div>
            </div>
          </Card>

          {/* Values */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-sky-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">100% Gratuit</h3>
              <p className="text-slate-500 text-sm">
                {lang === "fr" ? "Aucun frais caché, jamais." : "No hidden fees, ever."}
              </p>
            </Card>
            <Card className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">100% Local</h3>
              <p className="text-slate-500 text-sm">
                {lang === "fr" ? "Par et pour les Luxembourgeois." : "By and for Luxembourgers."}
              </p>
            </Card>
            <Card className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">100% Confiance</h3>
              <p className="text-slate-500 text-sm">
                {lang === "fr" ? "Avis vérifiés de la communauté." : "Verified community reviews."}
              </p>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button 
              onClick={() => navigate('/auth')}
              className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 rounded-xl text-lg font-semibold"
            >
              {lang === "fr" ? "Rejoindre la communauté" : "Join the community"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </main>
    </>
  );
};

export default AboutPage;
